import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import getMetrics from './getMetrics';
import { filterMetricsForTotals } from './filterMetricsForTotals';
import limitModeTypeCheckers from 'components/V5GadgetForm/limitModeTypeCheckers';

const getExcludeTopNTermsResponses = async ({
  chartDef,
  filterInput,
  usedMetrics,
  isDashboardGadget,
  currentDateScope,
  previousDateScope,
  toMetricInput,
  client,
  groupByField,
  comparison,
}: {
  chartDef: V5ChartDefinition;
  filterInput: FilterInput;
  usedMetrics: Metrics.Metric[];
  isDashboardGadget?: boolean;
  currentDateScope: DateRangeInput;
  comparison?: PersistedComparisonType;
  previousDateScope: DateRangeInput;
  toMetricInput: (metric: Metrics.NormalMetric | MetricInput) => MetricInput;
  client: ApolloClient<NormalizedCacheObject>;
  groupByField: string;
}) => {
  if (!limitModeTypeCheckers.isExcludeTopN(chartDef.groupByLimitMode)) {
    const e = new Error('Invalid groupByLimitMode');
    e.name = 'ExcludeTopNTerms: Invalid groupByLimitMode';
    throw e;
  }

  const groupedMetricsRequest = await getMetrics({
    chartDef,
    filterInput,
    usedMetrics,
    isDashboardGadget,
    dateScope: currentDateScope,
    toMetricInput,
    client,
    isIgnoringLimit: true,
  });

  const groupedMetricsComparisonRequest =
    comparison && chartDef.series.some((s) => !!s.matrixCellType)
      ? await getMetrics({
          chartDef,
          filterInput,
          usedMetrics,
          isDashboardGadget,
          dateScope: previousDateScope,
          toMetricInput,
          client,
          isIgnoringLimit: true,
        })
      : [];

  const { excludeSort, n } = chartDef.groupByLimitMode;

  let resultsToExclude: MetricsResponse = groupedMetricsRequest;
  // If excludeSort.field is a comparison, we sort and exclude using the comparison results.
  const foundMetric = chartDef.series.find(
    (s) =>
      s.metricId === excludeSort?.field ||
      `${s.metricId}-${s.smartCardComparison}` === excludeSort?.field,
  );

  const sortKey = foundMetric?.metricId || groupByField;
  resultsToExclude = resultsToExclude.sort((a, b) => {
    if (foundMetric?.metricId) {
      return excludeSort?.direction === 'asc'
        ? a[sortKey] - b[sortKey]
        : b[sortKey] - a[sortKey];
    }

    return excludeSort?.direction === 'asc'
      ? a[sortKey].localeCompare(b[sortKey])
      : b[sortKey].localeCompare(a[sortKey]);
  });

  const excludedTerms: string[] = [];
  resultsToExclude.forEach((result, idx) => {
    if (n && idx < n) {
      excludedTerms.push(result[groupByField]);
      return false;
    }
    return true;
  });

  let filteredGroupMetricsRequest = groupedMetricsRequest;
  let filteredGroupMetricsComparisonRequest = groupedMetricsComparisonRequest;
  filteredGroupMetricsRequest = groupedMetricsRequest.filter(
    (result) => !excludedTerms.includes(result[groupByField]),
  );
  filteredGroupMetricsComparisonRequest =
    groupedMetricsComparisonRequest.filter(
      (result) => !excludedTerms.includes(result[groupByField]),
    );

  const totalsFilterInput: FilterInput = {
    ...filterInput,
    keywords: [
      ...(filterInput.keywords || []),
      {
        field: groupByField,
        values: excludedTerms,
        exclude: true,
      },
    ],
  };

  const totalsMetrics = filterMetricsForTotals({ usedMetrics, chartDef });
  const totalsRequest =
    totalsMetrics.length > 0
      ? getMetrics({
          chartDef,
          filterInput: totalsFilterInput,
          getTotals: true,
          usedMetrics: totalsMetrics,
          isDashboardGadget,
          dateScope: currentDateScope,
          toMetricInput,
          client,
          isIgnoringLimit: true,
        })
      : Promise.resolve([]);
  const totalsComparisonRequest =
    comparison &&
    chartDef.series.some((s) => !!s.matrixCellType) &&
    totalsMetrics.length > 0
      ? getMetrics({
          chartDef,
          filterInput: totalsFilterInput,
          getTotals: true,
          usedMetrics: totalsMetrics,
          isDashboardGadget,
          dateScope: previousDateScope,
          toMetricInput,
          client,
          isIgnoringLimit: true,
        })
      : Promise.resolve([]);

  const responses = await Promise.all([
    filteredGroupMetricsRequest,
    totalsRequest,
    filteredGroupMetricsComparisonRequest,
    totalsComparisonRequest,
  ]);

  return responses;
};

export default getExcludeTopNTermsResponses;
